import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import instance from "../instance/AxiosInstance";
import { UserContext } from "./UserContext";
import {
  getDefaultLocation,
  getLocationFromStorage,
  saveLocationToStorage,
  isValidLocation
} from "../utilities/LocationUtils";

export const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
  const { User } = useContext(UserContext);

  const [location, setLocation] = useState(() => {
    // Initialize with default location (Kochi, India)
    return getDefaultLocation();
  });

  // Track if location update is in progress to prevent duplicate calls
  const isUpdatingRef = useRef(false);
  const lastUpdateRef = useRef(null);
  const hasInitialized = useRef(false);

  // Update location to backend whenever location changes (from search or geolocation)
  useEffect(() => {
    // Check if User exists and has _id before proceeding
    if (!User || !User._id) {
      console.log("⏳ User not loaded yet, skipping location update");
      return;
    }

    // Only update if we have valid coordinates
    if (!location.latitude || !location.longitude) {
      console.log("⚠️ Missing coordinates, skipping location update");
      return;
    }

    // Check if this is the same location as last update (prevent duplicate updates)
    const currentLocationKey = `${location.latitude},${location.longitude},${location.district}`;
    if (lastUpdateRef.current === currentLocationKey) {
      console.log("⚠️ Location unchanged, skipping duplicate update");
      return;
    }

    // Prevent concurrent updates
    if (isUpdatingRef.current) {
      console.log("⏳ Location update already in progress, skipping");
      return;
    }

    // Prepare location payload
    const locationPayload = {
      userId: User._id,
      location: {
        type: "Point",
        coordinates: [location.longitude, location.latitude],
      },
      city: location.city || null,
      district: location.district || null,
      state: location.state || null,
      country: location.country || null,
    };

    console.log("📍 Updating location to backend:", locationPayload);

    isUpdatingRef.current = true;

    instance.put('/api/addlocation', locationPayload)
      .then(res => {
        console.log("✅ Location updated successfully:", res.data);
        lastUpdateRef.current = currentLocationKey;
      })
      .catch(error => {
        console.error("❌ Failed to update location:", error.response?.data || error.message);

        // Optionally update location state with error
        setLocation(prev => ({
          ...prev,
          error: "Failed to save location to server"
        }));
      })
      .finally(() => {
        isUpdatingRef.current = false;
      });
  }, [location.latitude, location.longitude, location.city, location.district, location.state, location.country, User]);

  console.log("📌 Current location from context:", location);

  // Initialize location on mount based on user status
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    console.log("🚀 Initializing LocationContext");

    // If user is logged in, try to load their saved location
    if (User && User._id) {
      console.log("👤 User logged in, checking for saved location");
      instance
        .get(`/api/user/profile/get_profile/${User._id}`)
        .then((response) => {
          const userData = response.data;

          // Check if user has saved location
          if (userData.location && userData.location.coordinates) {
            const [lng, lat] = userData.location.coordinates;
            console.log("✅ User has saved location:", { lat, lng });

            setLocation({
              latitude: lat,
              longitude: lng,
              city: userData.address?.locality || null,
              district: userData.address?.district || null,
              state: userData.address?.state || null,
              country: "India",
              loading: false,
              error: null,
              isDefault: false,
              source: 'user_profile'
            });
          } else {
            console.log("ℹ️ User has no saved location, using default");
            // User is logged in but has no saved location - use default
            setLocation(getDefaultLocation());
          }
        })
        .catch((error) => {
          console.error("❌ Error loading user profile:", error);
          setLocation(getDefaultLocation());
        });
    } else {
      // Guest user - check localStorage for saved location
      console.log("👻 Guest user, checking localStorage");
      const savedGuestLocation = getLocationFromStorage();

      if (savedGuestLocation && isValidLocation(savedGuestLocation)) {
        console.log("✅ Found saved guest location:", savedGuestLocation);
        setLocation({
          ...savedGuestLocation,
          loading: false,
          error: null
        });
      } else {
        console.log("ℹ️ No saved guest location, using default");
        // No saved guest location - use default
        setLocation(getDefaultLocation());
      }
    }
  }, [User?._id]);

  // Get location details from coordinates using Google Geocoding API via Backend
  const getLocationDetails = async (latitude, longitude) => {
    try {
      console.log("🗺️ Fetching location details from Google Geocoding API via backend");

      const response = await instance.post('/api/location/reverse-geocode', {
        latitude,
        longitude,
      });

      const data = response.data;

      if (data.success && data.result) {
        console.log("✅ Google Geocoding API response:", data.result);

        return {
          cityName: data.result.city || "Unknown City",
          districtName: data.result.district || "Unknown District",
          stateName: data.result.state || "Unknown State",
          countryName: data.result.country || "Unknown Country",
        };
      } else {
        throw new Error(data.message || "Failed to fetch location details");
      }
    } catch (error) {
      console.error("❌ Error fetching location details from Google API:", error);
      throw error; // Re-throw to trigger fallback
    }
  };

  // Fallback to IP-based location
  const fetchFallbackLocation = async () => {
    try {
      console.log("🌐 Attempting IP-based location fallback...");

      const response = await fetch("https://ip-api.com/json/");
      const data = await response.json();

      if (data.status === 'success') {
        console.log("✅ IP-based location retrieved:", data);

        setLocation((prev) => ({
          ...prev,
          latitude: data.lat,
          longitude: data.lon,
          city: data.city || "Unknown City",
          district: data.regionName || "Unknown District",
          state: data.regionName || "Unknown State",
          country: data.country || "Unknown Country",
          loading: false,
          error: null,
        }));
      } else {
        throw new Error("IP-based location failed");
      }
    } catch (fallbackError) {
      console.error("❌ Fallback location error:", fallbackError);
      setLocation((prev) => ({
        ...prev,
        loading: false,
        error: "Failed to fetch location",
      }));
    }
  };

  // Custom setter that also saves to localStorage for guest users
  const updateLocation = (newLocation) => {
    setLocation(newLocation);

    // Save to localStorage for guest users
    if (!User || !User._id) {
      console.log("💾 Saving guest location to localStorage");
      saveLocationToStorage(newLocation);
    }
  };

  return (
    <LocationContext.Provider value={{ location, setLocation: updateLocation }}>
      {children}
    </LocationContext.Provider>
  );
};